// Bowel prep protocols supported by Dr. Kichian's clinic.
// Two distinct preps depending on the patient's medical history:
//   • pico-salax : the standard prep for most patients
//   • peglite    : the safer high-volume prep for patients with kidney
//                  disease, congestive heart failure, or liver disease
//                  (Pico-Salax is contraindicated in these patients).
//
// Each prep guide follows the same shape so the shared page renderer can
// display either one consistently.

export const PREP_GUIDES = [
    {
        slug: "pico-salax",
        product: "Pico-Salax",
        alt_names: "Purg-Odan",
        headline: "Pico-Salax bowel prep — step-by-step",
        tagline: "The standard prep prescribed for most patients.",
        who_is_this_for:
            "Most patients preparing for a colonoscopy with Dr. Kichian are asked to take Pico-Salax (or Purg-Odan). It is a low-volume prep that most people find easier to drink.",
        contraindications:
            "Pico-Salax is NOT safe for patients with kidney disease, congestive heart failure, liver disease with ascites, pregnancy, or after a colectomy. If you have any of these conditions, please call our office — you will be prescribed Peglite instead.",
        recommended_class: "most-patients",
        color_theme: "teal",
        cover_img: "/bowel-prep/step-4-prep-solution.png",
        steps: [
            {
                key: "1w",
                icon: "Stethoscope",
                img: "/bowel-prep/step-1-talk-to-doctor.png",
                title: "1 week before — Review your medications",
                summary:
                    "Go over all your medications and supplements with our team. Some may need to be adjusted before your prep.",
                bullets: [
                    "Call our office immediately if you take prescription blood thinners (Pradaxa, Eliquis, Xarelto, Coumadin/Warfarin, etc.), Aspirin (ASA), or anti-inflammatories.",
                    "You may continue all other medications, vitamins, and Tylenol as usual.",
                    "If you are diabetic, monitor your blood sugars carefully and use clear juice to help control them — ask your family doctor if unsure.",
                ],
            },
            {
                key: "3d",
                icon: "UtensilsCrossed",
                img: "/bowel-prep/step-2-low-fiber-food.png",
                title: "3 days before — Start the low-residue diet",
                summary:
                    "Switch to gentle, easy-to-digest foods so your colon empties more thoroughly on prep day.",
                bullets: [
                    "Avoid: red meat, raw vegetables, seeds and nuts of all kinds, corn, and milk products.",
                    "You can enjoy: eggs (not fried), pasta with cooked vegetables, seedless fruit, non-cream soups, crackers, lean chicken or fish, white bread, white rice, potatoes, plain cookies, Jell-O (not red or purple).",
                    "At bedtime: take 1 Dulcolax laxative (5 mg bisacodyl tablet).",
                ],
            },
            {
                key: "2d",
                icon: "Pill",
                img: "/bowel-prep/step-3-clear-liquids.png",
                title: "2 days before — Continue the diet",
                summary:
                    "Stay on the same low-residue diet. Take one more Dulcolax tablet at bedtime.",
                bullets: [
                    "Same food list as day 3 — no red meat, raw veg, seeds, nuts, corn, or milk products.",
                    "At bedtime: take 1 more Dulcolax laxative (5 mg bisacodyl tablet).",
                    "Keep drinking water through the day — hydration makes the prep easier.",
                ],
            },
            {
                key: "1d",
                icon: "GlassWater",
                img: "/bowel-prep/step-3-clear-liquids.png",
                title: "1 day before — Clear liquids only",
                summary:
                    "No solid food today. Only clear fluids — this is the most important rule.",
                bullets: [
                    "Clear fluids: water, Gatorade (or similar electrolyte drink), clear broth (up to 1 L), ginger ale, Jell-O (not red or purple), clear juices, popsicles, tea or coffee with NO milk, cream, or soy.",
                    "Absolutely NO solids are permitted from now until after your procedure.",
                    "If Gatorade is hard to tolerate, dilute it with any clear liquid, or try Gastrolyte (available at any pharmacy).",
                ],
            },
            {
                key: "dose1-am",
                icon: "AlarmClock",
                img: "/bowel-prep/step-4-prep-solution.png",
                title: "Morning appointment — First Pico-Salax dose at 8:00 AM (day before)",
                summary:
                    "If your procedure is between 8:00 AM and 11:00 AM, take the first Pico-Salax envelope in the morning the day before.",
                bullets: [
                    "At 8:00 AM the day before: prepare and drink the 1st envelope of Pico-Salax.",
                    "Then drink 2 L of clear fluid — a combination of any clear fluid plus one bottle of a sports drink — over the next 4 hours.",
                    "Stay near a bathroom — frequent bowel movements are expected and normal.",
                ],
            },
            {
                key: "dose2-am",
                icon: "AlarmClock",
                img: "/bowel-prep/step-5-second-dose-morning.png",
                title: "Morning appointment — Second Pico-Salax dose at 5:00 PM (day before)",
                summary:
                    "For morning procedures, take the second envelope in the early evening the day before your procedure.",
                bullets: [
                    "At 5:00 PM the day before: drink the 2nd envelope of Pico-Salax.",
                    "Follow with 2 L of clear fluid (any clear fluid + one bottle of sports drink) over 4 hours.",
                    "You may continue clear fluids right up to your hospital arrival time.",
                ],
            },
            {
                key: "dose1-pm",
                icon: "AlarmClock",
                img: "/bowel-prep/step-4-prep-solution.png",
                title: "Afternoon appointment — First Pico-Salax dose at 5:00 PM (day before)",
                summary:
                    "If your procedure is between 11:30 AM and 2:30 PM, take the first envelope in the early evening the day before.",
                bullets: [
                    "At 5:00 PM the day before: prepare and drink the 1st envelope of Pico-Salax.",
                    "Follow with 2 L of clear fluid (any clear fluid + one bottle of sports drink) over the next 4 hours.",
                ],
            },
            {
                key: "dose2-pm",
                icon: "AlarmClock",
                img: "/bowel-prep/step-5-second-dose-morning.png",
                title: "Afternoon appointment — Second Pico-Salax dose the next morning",
                summary:
                    "For afternoon procedures, take the second envelope about 5–6 hours before your scheduled appointment.",
                bullets: [
                    "About 5–6 hours before your appointment: drink the 2nd envelope of Pico-Salax.",
                    "Follow with 2 L of clear fluid (any clear fluid + one bottle of sports drink) over 4 hours.",
                    "You may continue clear fluids right up to hospital arrival.",
                ],
            },
            {
                key: "day-of",
                icon: "Building2",
                img: "/bowel-prep/step-6-arrival-day.png",
                title: "Procedure day — Arrive and relax",
                summary:
                    "Come to Queensway Carleton Hospital 1 hour before your appointment with a responsible adult driver.",
                bullets: [
                    "Bring your OHIP card, an updated list of medications, a bag for belongings, and a small snack and drink for after.",
                    "Sedation is strongly recommended. If sedated, you cannot legally drive for 24 hours and must have someone drive you home — taxi alone is against hospital policy.",
                    "Please do not wear cologne, perfume, or other scents on the day of your procedure (deodorant is fine).",
                ],
            },
        ],
    },
    {
        slug: "peglite",
        product: "Peglite",
        alt_names: "Colyte (also acceptable)",
        headline: "Peglite bowel prep — step-by-step",
        tagline: "The safer prep for patients with kidney or heart disease.",
        who_is_this_for:
            "Peglite (or Colyte) is the prep prescribed to patients who cannot safely take Pico-Salax — this includes patients with kidney disease, congestive heart failure, liver disease with ascites, pregnancy, or a prior colectomy. It is a higher-volume prep (4 L) but is gentler on the kidneys and heart because it is balanced with electrolytes.",
        contraindications:
            "If you are unsure which prep you should be taking, please call our office. Do not switch preps on your own.",
        recommended_class: "kidney-heart",
        color_theme: "navy",
        cover_img: "/bowel-prep/step-4-prep-solution.png",
        steps: [
            {
                key: "1w",
                icon: "Stethoscope",
                img: "/bowel-prep/step-1-talk-to-doctor.png",
                title: "1 week before — Review your medications",
                summary:
                    "Go over all your medications and supplements with our team. Some may need to be adjusted before your prep.",
                bullets: [
                    "Call our office immediately if you take prescription blood thinners (Pradaxa, Eliquis, Xarelto, Coumadin/Warfarin, etc.), Aspirin (ASA), or anti-inflammatories.",
                    "You may continue all other medications, vitamins, and Tylenol as usual.",
                    "If you are diabetic, monitor your blood sugars carefully and use clear juice to help control them.",
                    "Tell the hospital team on the day of your procedure if you have a heart condition or need antibiotics before medical procedures (for example after joint replacement).",
                ],
            },
            {
                key: "3d",
                icon: "UtensilsCrossed",
                img: "/bowel-prep/step-2-low-fiber-food.png",
                title: "3 days before — Start the low-residue diet",
                summary:
                    "Switch to gentle, easy-to-digest foods so your colon empties more thoroughly on prep day.",
                bullets: [
                    "Avoid: red meat, raw vegetables, seeds and nuts of all kinds, corn, and milk products.",
                    "You can enjoy: eggs (not fried), pasta with cooked vegetables, seedless fruit, non-cream soups, crackers, lean chicken or fish, white bread, white rice, potatoes, plain cookies, Jell-O (not red).",
                    "At bedtime: take 1 Dulcolax laxative (5 mg bisacodyl tablet).",
                ],
            },
            {
                key: "2d",
                icon: "Pill",
                img: "/bowel-prep/step-3-clear-liquids.png",
                title: "2 days before — Continue the diet",
                summary:
                    "Stay on the same low-residue diet. Take one more Dulcolax tablet at bedtime.",
                bullets: [
                    "Same food list as day 3 — no red meat, raw veg, seeds, nuts, corn, or milk products.",
                    "At bedtime: take 1 more Dulcolax laxative (5 mg bisacodyl tablet).",
                    "Stay well hydrated during the day.",
                ],
            },
            {
                key: "1d",
                icon: "GlassWater",
                img: "/bowel-prep/step-3-clear-liquids.png",
                title: "1 day before — Clear liquids only",
                summary:
                    "No solid food today. Only clear fluids — this is the most important rule.",
                bullets: [
                    "Clear fluids: water, Gatorade or similar electrolyte drink, broth, ginger ale, Jell-O (not red), clear juices, popsicles, tea or coffee.",
                    "Absolutely NO solids are permitted from now until after your procedure.",
                    "Chill the Peglite in the fridge — cold prep is much easier to drink.",
                ],
            },
            {
                key: "dose-am",
                icon: "AlarmClock",
                img: "/bowel-prep/step-4-prep-solution.png",
                title: "Morning appointment — Drink the full 4 L at 5:00 PM (day before)",
                summary:
                    "If your procedure is between 8:00 AM and 11:00 AM, drink the entire 4 L of Peglite the evening before.",
                bullets: [
                    "At 5:00 PM the day before: prepare the full 4 L of Peglite (or Colyte).",
                    "Drink the full 4 L steadily over about a 2-hour period — roughly one 250 mL glass every 10–15 minutes.",
                    "Sip through a straw and keep the solution cold to make it easier.",
                ],
            },
            {
                key: "dose1-pm",
                icon: "AlarmClock",
                img: "/bowel-prep/step-4-prep-solution.png",
                title: "Afternoon appointment — First 2 L at 5:00 PM (day before)",
                summary:
                    "For afternoon procedures, split the 4 L into two doses. Start with 2 L in the evening.",
                bullets: [
                    "At 5:00 PM the day before: drink 2 L of Peglite (or Colyte).",
                    "Sip steadily over about an hour — the cold solution goes down much easier.",
                ],
            },
            {
                key: "dose2-pm",
                icon: "AlarmClock",
                img: "/bowel-prep/step-5-second-dose-morning.png",
                title: "Afternoon appointment — Second 2 L at 6:00 AM (procedure day)",
                summary:
                    "Finish the remaining 2 L in the early morning of your procedure.",
                bullets: [
                    "At 6:00 AM on procedure day: drink the remaining 2 L of Peglite.",
                    "You may continue drinking clear fluids right up to hospital arrival.",
                ],
            },
            {
                key: "day-of",
                icon: "Building2",
                img: "/bowel-prep/step-6-arrival-day.png",
                title: "Procedure day — Arrive and relax",
                summary:
                    "Come to Queensway Carleton Hospital 1 hour before your appointment with a responsible adult driver.",
                bullets: [
                    "Bring your OHIP card, an updated list of medications, a bag for belongings, and a small snack and drink for after.",
                    "Sedation is strongly recommended. If sedated, you cannot legally drive for 24 hours and must have someone drive you home — taxi alone is against hospital policy.",
                    "Please do not wear cologne, perfume, or other scents (deodorant is fine).",
                ],
            },
        ],
    },
];

export function getPrepGuide(slug) {
    return PREP_GUIDES.find((g) => g.slug === slug);
}

export const YOU_KNOW_READY_WHEN = [
    "Your stool is a clear yellow liquid — like pale tea or lemonade.",
    "There are no more solid pieces or dark bits.",
    "You have finished all of the prep as prescribed, not just \"most of it\".",
];

export const RED_FLAGS = [
    "Uncontrolled vomiting that prevents you from finishing the prep — call our office.",
    "Severe abdominal pain that does not improve — call our office, or 911 if severe.",
    "Signs of dehydration (dizziness, no urination, confusion) — sip clear liquids and call us.",
];

// CLINICAL REVIEW NEEDED — French translations of the readiness checklist and
// red-flag list. Bilingual medical review recommended before publication.
export const YOU_KNOW_READY_WHEN_FR = [
    "Vos selles sont un liquide jaune clair — comme du thé pâle ou de la limonade.",
    "Il n'y a plus de morceaux solides ni de particules sombres.",
    "Vous avez terminé toute la préparation telle que prescrite, et non pas seulement « la plupart ».",
];

export const RED_FLAGS_FR = [
    "Vomissements incontrôlés qui vous empêchent de terminer la préparation — appelez notre bureau.",
    "Douleur abdominale sévère qui ne s'améliore pas — appelez notre bureau, ou le 911 si sévère.",
    "Signes de déshydratation (étourdissements, absence d'urine, confusion) — buvez des liquides clairs et appelez-nous.",
];
